import { FC } from "react";
import { OrganizationIntegrationAccountItem } from "@common/schemas/organization-connection-groups.schema";
import { ORGANIZATION_INTEGRATION_STATUS_LABELS } from "@common/helpers/organizationIntegrationStatus";
import { HStack } from "@design-system/HStack/HStack";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { timeAgo } from "@helpers/date";
import { DocumentIcon } from "@icons";
import {
    getIntegrationStatusSyncedAt,
    INTEGRATION_STATUS_ICONS,
    resolveIntegrationStatus,
    STATEMENTS_INDICATOR_LABEL,
} from "./integrationStatus";
import styles from "./IntegrationStatusCell.module.scss";

const StatementsIndicator: FC = () => (
    <Tooltip content="Bank statements are available for this account">
        <span
            className={styles.statementsIcon}
            role="img"
            aria-label={STATEMENTS_INDICATOR_LABEL}
        >
            <DocumentIcon />
        </span>
    </Tooltip>
);

export const IntegrationStatusCell: FC<{
    item: OrganizationIntegrationAccountItem;
}> = ({ item }) => {
    const status = resolveIntegrationStatus(item);
    const Icon = INTEGRATION_STATUS_ICONS[status];
    const { hasStatements } = item.account;

    // Statement availability is a fact about the account, not about its
    // status, so it still shows on a row whose status renders no glyph.
    if (!Icon) {
        return hasStatements ? <StatementsIndicator /> : null;
    }

    const label = ORGANIZATION_INTEGRATION_STATUS_LABELS[status];
    const syncedAt = getIntegrationStatusSyncedAt(item);

    return (
        <HStack gap={8} align="center">
            <Tooltip
                content={
                    syncedAt ? `${label} · synced ${timeAgo(syncedAt)}` : label
                }
            >
                <span className={styles.icon} role="img" aria-label={label}>
                    <Icon />
                </span>
            </Tooltip>
            {syncedAt ? (
                <span className={styles.recency}>
                    {timeAgo(syncedAt, { hideAgo: true })}
                </span>
            ) : null}
            {hasStatements ? <StatementsIndicator /> : null}
        </HStack>
    );
};
